import React from 'react';
import { Coins, ExternalLink } from 'lucide-react';

const TOKEN_PRODUCT_URL = process.env.REACT_APP_TOKEN_PRODUCT_URL || '';
const TOKEN_VALUE_USD = 5;

/**
 * CoursePricing — Token Model Display
 * ------------------------------------
 * Architect directive (2026-05-27 Token Reboot):
 *   • Display every course price as "N Tokens / $X" where 1 Token = $5
 *   • Provide a single "Buy Credits" CTA → ONE universal Gumroad token product
 *   • Eliminate per-course Gumroad links from the user-facing flow
 *   • Redeem Code Panel below is the actual fulfillment gate
 */
export default function CoursePricing({ priceUsd }) {
  if (!priceUsd || priceUsd <= 0) return null;
  const tokens = Math.ceil(priceUsd / TOKEN_VALUE_USD);
  const ready = Boolean(TOKEN_PRODUCT_URL);
  // Architect directive (2026-05-31): append ?quantity=N so Gumroad
  // pre-fills the token quantity that matches this course's price.
  const buyUrl = ready
    ? `${TOKEN_PRODUCT_URL}${TOKEN_PRODUCT_URL.includes('?') ? '&' : '?'}quantity=${tokens}`
    : '';

  return (
    <section
      data-testid="course-pricing-tokens"
      className="border-y border-amber-500/20 bg-gradient-to-b from-black via-amber-950/10 to-black py-10"
    >
      <div className="container mx-auto px-4 max-w-3xl text-center">
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-amber-400/80 mb-3">
          Federation Tokens · 1 Token = ${TOKEN_VALUE_USD} · Books, Courses & more
        </div>

        <div className="flex items-baseline justify-center gap-3 flex-wrap mb-2">
          <span
            data-testid="course-token-count"
            className="text-4xl md:text-5xl font-bold text-amber-300"
            style={{ textShadow: '0 0 24px rgba(251, 191, 36, 0.4)' }}
          >
            {tokens} Tokens
          </span>
          <span className="text-2xl text-gray-400 font-mono">/</span>
          <span
            data-testid="course-usd-price"
            className="text-2xl text-gray-300 font-mono"
          >
            ${priceUsd.toFixed(0)}
          </span>
        </div>

        <p className="text-sm text-gray-400 mt-3 mb-6 max-w-xl mx-auto">
          Buy {tokens} Federation Credits on Gumroad, then paste your access code into the
          Redeem panel below to unlock this course. Same credits work on books and any
          future Federation offering.
        </p>

        {ready ? (
          <a
            href={buyUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="buy-credits-cta"
            data-gumroad-quantity={tokens}
            className="inline-flex items-center gap-2 bg-amber-400 text-black font-bold px-7 py-3 rounded-lg hover:bg-amber-300 transition-colors"
          >
            <Coins className="w-5 h-5" />
            Buy {tokens} Tokens — ${priceUsd.toFixed(0)}
            <ExternalLink className="w-4 h-4" />
          </a>
        ) : (
          <div
            data-testid="buy-credits-pending"
            className="inline-flex items-center gap-2 bg-gray-800 text-gray-400 font-bold px-7 py-3 rounded-lg cursor-not-allowed"
            title="Awaiting REACT_APP_TOKEN_PRODUCT_URL in /app/frontend/.env"
          >
            <Coins className="w-5 h-5 opacity-50" />
            Buy Credits — link pending
          </div>
        )}

        <p className="text-xs text-gray-500 mt-4">
          Same Token works on every course · 30-day money-back guarantee
        </p>
      </div>
    </section>
  );
}
